<template>
	<div class="cog-stripe-payment">
		<stripe-card-placeholder v-if="showLoader" :mobile="mobile" />
		<stripe-card
			ref="card"
			:publishable-key="apiKey"
			:locale="$culture.name"
			:input-styles="inputStyles"
			:amount="orderAmountCents"
			:save-card="saveCard"
			:payment-required="paymentRequired"
			:currency="currency"
			:billing-details="billingDetails"
			:use-wallets="useWallets"
			@ready="handlePaymentElementReady"
			@focus="handleFocus"
			@blur="handleBlur"
			@change="handleChange"
		>
			<div id="cog-payment-element" class="cog-payment__frame cog-field">
			</div>
		</stripe-card>
	</div>
</template>

<script>
	import StripeCard from './StripeCard';
	import StripeCardPlaceholder from './StripeCardPlaceholder.vue';
	import { FriendlyError } from '../../framework/logging/error';
	import { removeEmptyProps } from 'src/util/helpers';
	import { toCents } from 'src/util/currency';
	import OrderBuilder from '../../framework/payment/order-builder';
	import { PaymentTokenStatus } from '@cognitoforms/types/server-types/payment/model/payment-token-status';

	export default {
		name: 'CStripePayment',
		components: {
			StripeCard,
			StripeCardPlaceholder
		},
		inject: ['$resource', 'log', 'session', '$culture'],
		props: {
			entry: { type: Object, required: true },
			orderInfo: { type: Object, default: null },
			apiKey: { type: String, required: true },
			currencyCode: { type: String, default: 'USD' },
			inputStyles: { type: Object, default: null },
			saveCard: { type: Boolean, default: false },
			paymentRequired: { type: Boolean, default: false },
			mobile: { type: Boolean, default: null },
			useWallets: { type: Boolean, default: true }
		},
		data: function() {
			return {
				showLoader: true,
				creatingToken: false,
				temporaryPaymentToken: null
			};
		},
		computed: {
			order() {
				return this.entry.Order;
			},
			orderAmount() {
				return this.order.OrderAmount;
			},
			paymentToken() {
				return this.entry.Entry.PaymentToken;
			},
			orderAmountCents() {
				if (this.orderAmount)
					return toCents(this.orderAmount, {
						NumberOfDecimals: this.$culture.numberFormat.CurrencyDecimalDigits
					});
				else
					return 0;
			},
			currency() {
				return this.currencyCode ? this.currencyCode.toLowerCase() : null;
			},
			billingDetails() {
				if (!this.orderInfo)
					return null;

				/**
				 * @type {StripeCard.BillingDetails}
				 */
				const billingDetails = {};

				if (this.orderInfo.Name && (this.orderInfo.Name.First || this.orderInfo.Name.Last)) {
					billingDetails.name = this.orderInfo.Name.First + ' ' + this.orderInfo.Name.Last;
				}
				if (this.orderInfo.Address) {
					billingDetails.address = {
						line1: this.orderInfo.Address.Line1,
						line2: this.orderInfo.Address.Line2,
						city: this.orderInfo.Address.City,
						state: this.orderInfo.Address.State,
						postal_code: this.orderInfo.Address.PostalCode,
						country: this.orderInfo.Address.CountryCode
					};
				}

				billingDetails.email = this.orderInfo.Email;
				billingDetails.phone = this.orderInfo.Phone;

				return removeEmptyProps(billingDetails);
			}
		},
		destroyed() {
			// The 'temporaryPaymentToken' is a payment token created internally by the payment component as the
			// credit card fields are interacted with (as soon as they are all filled out). If that is the current
			// payment token, then it should be cleared out since the token was not created in the context of submitting
			// the form, ex: the payment component is being destroyed due to a change that causes payment not to be required.
			if (this.temporaryPaymentToken && this.entry.Entry.PaymentToken === this.temporaryPaymentToken)
				this.entry.Entry.PaymentToken = null;
		},
		methods: {
			/**
			 * Create a payment intent when the form is submitted,
			 * or when multi-fees (ex: domestic vs. international) can be calculated.
			 * @returns { Promise<import('../../framework/payment/payment-types').PaymentTokenInfo> } - The token that was created
			 */
			async createToken() {
				if (this.creatingToken) {
					// If we're currently in process of creating a token, then wait until complete
					await new Promise((resolve, reject) => {
						let createTokenAttempts = 0;
						const createTokenInterval = setInterval(() => {
							if (!this.creatingToken) {
								clearInterval(createTokenInterval);
								resolve();
							}
							else if (++createTokenAttempts > 10) {
								clearInterval(createTokenInterval);
								reject(new Error(this.$resource('order-submit-timeout')));
							}
						}, 100);
					});
				}

				let paymentMethod = null;

				// Signal  that we're creating a token so that we don't double-up requests
				this.creatingToken = true;
				try {
					/**
					 * @type {StripeCard}
					 */
					const stripeCard = this.$refs.card;
					if (!stripeCard) {
						throw new Error('Couldn\'t find Stripe payment form.');
					}

					paymentMethod = await stripeCard.capturePaymentMethod().then(response => {
						if (!response.error) {
							// Return the payment method from the response
							return response.paymentMethod;
						}
						else {
							let message = '';
							if (response.error) {
								if (response.error.code) {
									try {
										message = this.$resource(response.error.code);
									}
									catch {
										// Ignore error since resource will not be defined for all Stripe error codes
									}
								}
							}
							if (!message)
								message = this.$resource('credit-card-invalid');
							if (!message && response.error)
								message = response.error.message;
							this.creatingToken = false;
							throw new Error(message);
						}
					});
				}
				catch (e) {
					this.creatingToken = false;
					this.log.error(e, { method: 'StripePayment.createToken' });

					if (e.name && e.name === 'IntegrationError') {
						// Don't show integration errors to the end-user
						throw new FriendlyError('An error occurred');
					}
					// If it's an error
					else if (Object.prototype.toString.call(e) === '[object Error]' || (typeof e.message === 'string' && typeof e.stack === 'string')) {
						e.log = false;
						throw e;
					}
					else {
						let message = 'Unknown error';
						if (e) {
							if (e.message)
								message = e.message;
							else {
								// Don't use toString version of the object if it is simply the type representation
								const errorToString = e.toString();
								if (errorToString !== Object.prototype.toString.call(e))
									message = errorToString;
							}
						}
						throw new FriendlyError(message);
					}
				}

				// Signal that we're done creating the token
				this.creatingToken = false;

				return {
					Type: 'PaymentMethod',
					Token: paymentMethod.id,
					DateCreated: new Date(),
					Status: this.paymentRequired ? 'PendingPayment' : this.saveCard ? 'PendingSaveCard' : null,
					SaveForFutureUse: this.saveCard,
					IsLive: paymentMethod.livemode,
					Card: {
						Country: paymentMethod.card.country,
						Brand: paymentMethod.card.brand,
						Last4: paymentMethod.card.last4
					}
				};
			},

			handlePaymentElementReady(e) {
				this.$emit('ready');
				this.showLoader = false;
			},

			handleFocus(event) {
				/**
				 * @type import('../../framework/payment/payment-types').PaymentFieldEvent
				 */
				const focusEvent = {
					type: 'focus',
					sender: this,
					originalEvent: event,
					originalSender: this.$refs.card
				};
				this.$emit('focus', focusEvent);
			},

			handleBlur(event) {
				/**
				 * @type import('../../framework/payment/payment-types').PaymentFieldEvent
				 */
				const blurEvent = {
					type: 'blur',
					sender: this,
					originalEvent: event,
					originalSender: this.$refs.card
				};
				this.$emit('blur', blurEvent);
			},

			async handleChange(event) {
				// Clear out a cached payment method response if any of the credit card fields change
				this.paymentMethodResponse = null;

				const formEntry = this.entry;
				const currentToken = formEntry.Entry.PaymentToken;

				// Check to see if all payment fields on the form have been entered
				const isComplete = this.$refs.card.elementStatus.isComplete && !this.$refs.card.elementStatus.isEmpty;

				// Create a token if the card information is complete
				if (isComplete) {
					const orderBuilder = new OrderBuilder(this.entry);
					if (orderBuilder.processingFees && orderBuilder.processingFees.length > 1) {
						try {
							const token = await this.createToken();
							this.entry.Entry.update('PaymentToken', token);
							this.temporaryPaymentToken = this.entry.Entry.PaymentToken;
						}
						catch (error) {
							this.log.error(error);
							// If failed to create a token, then clear out an existing one
							if (currentToken)
								formEntry.Entry.PaymentToken = null;
						}
					}
				}
				// Otherwise, clear out the token if one existed previously
				else {
					if (currentToken) {
						formEntry.Entry.PaymentToken = null;
					}
				}
			},

			/**
			 * Attempts to authenticate the current payment or card using the
			 * payment token provided by the server
			 */
			async authenticate() {
				if (!this.paymentToken)
					return;

				if (!this.paymentToken.ClientSecret) {
					this.$emit('authentication-complete', { success: false });
					return;
				}

				const clientSecret = this.paymentToken.ClientSecret;
				const component = this.$refs.card;

				if (this.paymentToken.Type === 'SetupIntent') {
					const result = await component.authenticateCard(clientSecret);

					this.authenticationComplete({
						success: !result.error,
						status: result.error ? PaymentTokenStatus.AuthenticationFailed : PaymentTokenStatus.AuthenticationSucceeded,
						token: result.setupIntent ? result.setupIntent.id : null,
						type: 'SetupIntent'
					});
				}
				else {
					const result = await component.authenticatePayment(clientSecret);
					console.log(result);

					this.authenticationComplete({
						success: !result.error,
						status: result.error ? PaymentTokenStatus.AuthenticationFailed : result.paymentIntent.status === 'succeeded' ? PaymentTokenStatus.Confirmed : PaymentTokenStatus.AuthenticationSucceeded,
						token: result.paymentIntent ? result.paymentIntent.id : null,
						type: 'PaymentIntent'
					});
				}
			},

			/**
			 * @param event { import('../../framework/payment/payment-types').PaymentProcessorAuthenticationCompleteEvent }
			 */
			authenticationComplete(event) {
				if (event.token) {
					// Update the payment token on the entry
					this.entry.Entry.PaymentToken.update({
						Status: event.status,
						Type: event.type,
						Token: event.token,
						ClientSecret: event.clientSecret,
						VerificationToken: event.verificationToken
					});
				}

				// Only the success flag (and optional resubmit) is needed beyond this point
				this.$emit('authentication-complete', { success: event.success, forceResubmit: event.forceResubmit });
			}
		}
	};
</script>
